/**
 * Rate Limiter for Error Reporting
 *
 * Prevents flooding error logging endpoints with duplicate errors.
 * Uses a sliding window algorithm with error fingerprinting.
 *
 * @see /docs/ERROR_BOUNDARY_IMPROVEMENTS.md Phase 1 - Week 2
 */

export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  keyPrefix?: string;
}

export interface RateLimitResult {
  allowed: boolean;
  retryAfter?: number;
  currentCount: number;
  limit: number;
}

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

/**
 * Rate Limiter using sliding window algorithm
 */
export class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();
  private config: Required<RateLimitConfig>;
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(config: RateLimitConfig) {
    this.config = {
      keyPrefix: 'ratelimit',
      ...config,
    };

    // Start cleanup interval (every 5 minutes)
    if (typeof window !== 'undefined') {
      this.cleanupInterval = setInterval(() => this.cleanup(), 5 * 60 * 1000);
    }
  }

  /**
   * Check if a request is allowed
   *
   * @param key - Unique key for rate limiting (e.g., error fingerprint, user ID)
   * @returns Rate limit result with allowed status
   *
   * @example
   * ```typescript
   * const limiter = new RateLimiter({ maxRequests: 10, windowMs: 60000 });
   * const result = limiter.check('user-123');
   * if (!result.allowed) {
   *   console.log(`Rate limited. Retry after ${result.retryAfter}ms`);
   * }
   * ```
   */
  check(key: string): RateLimitResult {
    const fullKey = `${this.config.keyPrefix}:${key}`;
    const now = Date.now();
    const entry = this.limits.get(fullKey);

    // No entry or expired - allow and create new entry
    if (!entry || now >= entry.resetTime) {
      this.limits.set(fullKey, {
        count: 1,
        resetTime: now + this.config.windowMs,
      });

      return {
        allowed: true,
        currentCount: 1,
        limit: this.config.maxRequests,
      };
    }

    // Entry exists and not expired
    if (entry.count < this.config.maxRequests) {
      entry.count++;
      return {
        allowed: true,
        currentCount: entry.count,
        limit: this.config.maxRequests,
      };
    }

    // Rate limit exceeded
    return {
      allowed: false,
      retryAfter: entry.resetTime - now,
      currentCount: entry.count,
      limit: this.config.maxRequests,
    };
  }

  /**
   * Reset rate limit for a specific key
   */
  reset(key: string): void {
    const fullKey = `${this.config.keyPrefix}:${key}`;
    this.limits.delete(fullKey);
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.limits.entries()) {
      if (now >= entry.resetTime) {
        this.limits.delete(key);
      }
    }
  }

  /**
   * Destroy the rate limiter and clean up resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.limits.clear();
  }

  /**
   * Get current stats
   */
  getStats(): {
    totalKeys: number;
    activeKeys: number;
  } {
    const now = Date.now();
    let activeKeys = 0;

    for (const entry of this.limits.values()) {
      if (now < entry.resetTime) {
        activeKeys++;
      }
    }

    return {
      totalKeys: this.limits.size,
      activeKeys,
    };
  }
}

/**
 * Create a rate limiter for error reporting
 * Limit: 10 errors per minute per error fingerprint
 *
 * Prevents flooding error logging endpoints with duplicate errors.
 * Works with error fingerprinting to deduplicate similar errors.
 */
export const errorReportingLimiter = new RateLimiter({
  maxRequests: 10,
  windowMs: 60 * 1000, // 1 minute
  keyPrefix: 'error-report',
});
